import { useEffect, useState } from 'react';
import { wikiSearch, WikiSearchHit, WikiSearchResponse } from '../api/wiki';

export function SearchView({ pid }: { pid: string }) {
  const [q, setQ] = useState(() => {
    const m = (typeof window !== 'undefined' ? window.location.hash : '').match(
      /\?q=([^&]+)/,
    );
    return m ? decodeURIComponent(m[1]) : '';
  });
  const [mode, setMode] = useState<'hybrid' | 'bm25' | 'vector'>('hybrid');
  const [resp, setResp] = useState<WikiSearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await wikiSearch(pid, q.trim(), mode, 20);
      setResp(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (q) void submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <a href={`#/wiki/p/${encodeURIComponent(pid)}`}>← Project</a>
      <h2 style={{ marginTop: 8 }}>Search</h2>
      <form onSubmit={submit} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search pages and source spans…"
          style={{ flex: 1, padding: 8 }}
        />
        <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
          <option value="hybrid">Hybrid</option>
          <option value="bm25">BM25</option>
          <option value="vector">Vector</option>
        </select>
        <button type="submit" disabled={busy || !q.trim()}>
          {busy ? '…' : 'Search'}
        </button>
      </form>
      {error ? <div className="banner banner-warning">{error}</div> : null}
      {resp ? (
        <>
          <div className="muted small" style={{ marginBottom: 12 }}>
            {resp.results.length} results · {resp.took_ms} ms
            {resp.degraded ? ' · BM25-only (Vectorize unavailable)' : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {resp.results.map((hit, i) => (
              <SearchHitCard key={i} hit={hit} pid={pid} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function SearchHitCard({ hit, pid }: { hit: WikiSearchHit; pid: string }) {
  if (hit.kind === 'page' && hit.page_slug) {
    return (
      <a
        href={`#/wiki/p/${encodeURIComponent(pid)}/page/${encodeURIComponent(hit.page_slug)}`}
        style={{
          display: 'block',
          padding: 12,
          border: '1px solid var(--border, #444)',
          borderRadius: 6,
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <div style={{ fontWeight: 600 }}>
          📄 {hit.page_title}{' '}
          <span className="muted small">[{hit.page_kind}]</span>
        </div>
        <div className="muted small">{hit.page_slug}</div>
        {hit.snippet ? (
          <div
            style={{ marginTop: 6 }}
            dangerouslySetInnerHTML={{ __html: hit.snippet }}
          />
        ) : null}
        {hit.supporting && hit.supporting.length > 0 ? (
          <div className="muted small" style={{ marginTop: 6 }}>
            +{hit.supporting.length} supporting source spans
          </div>
        ) : null}
      </a>
    );
  }
  if (hit.kind === 'source_span' && hit.source_table && hit.source_id) {
    const rid = `${hit.source_table}:${hit.source_id}`;
    return (
      <a
        href={`#/wiki/p/${encodeURIComponent(pid)}/source/${encodeURIComponent(rid)}?span=${hit.span_start}-${hit.span_end}`}
        style={{
          display: 'block',
          padding: 12,
          border: '1px solid var(--border, #444)',
          borderRadius: 6,
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <div style={{ fontWeight: 600 }}>📑 {hit.source_title}</div>
        <div className="muted small">
          {hit.source_table} · span {hit.span_start}–{hit.span_end}
        </div>
        {hit.snippet ? (
          <div
            style={{ marginTop: 6 }}
            dangerouslySetInnerHTML={{ __html: hit.snippet }}
          />
        ) : null}
      </a>
    );
  }
  return null;
}
